import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { usePrevious } from '../../hooks/usePrevious';
import getRoleGroupByRole from '../../utils/dashboard/getRoleGroupbyRole';
import NavBarContainer from '../common/NavBarContainer';
import { configs } from '../../constants/configs/configs';
import SideNav from '../../components/individualPortal/navigation/SideNavigation';
import DelegationView from '../../components/delegation/DelegationView';
import {
    fields,
    labelNames,
    filtersByRole,
    tableHeadersByRole,
    DELEGATION_REASONS
} from '../../constants/delegation/delegationConfig';
import { USER_GROUPS, ALL_USER_ROLES } from '../../constants/roles/userRoleTypes';
import { brokerNavLinks } from '../individualPortal/navigation/configs/brokerSideNavConfigs';
import { assisterNavLinks } from '../individualPortal/navigation/configs/assisterSideNavConfigs';
import {
    fetchDelegationData,
    initDelegationFilters,
    setFilterValue,
    resetFilterValues,
    fetchNavBarData,
    fetchLeftNavParams
} from '../../actions/delegation/delegationActionCreators';

import { useIntl } from 'react-intl';

const DelegationContainer = props => {
    const { endpoints, externalEndpoints } = configs;
    const {
        actions,
        spinner,
        environment: {
            stateCode,
            isState,
            impersonation,
            impersonationDetails,  
            user: {
                role
            },
            language,
            links,
            ticketEnabled,
            designationHistoryEnabled,
            flags: {
                enrollmentDetailsEnabled
            }
        },
        delegation: {
            history,
            filters,
            pagination,
            leftNavParams
        }
    } = props;

    const intlApi = useIntl();
    
    const roleGroup = !impersonation ? getRoleGroupByRole(role) : getRoleGroupByRole(impersonationDetails.role);

    const createFilterObj = arr => arr.reduce((o, key) => ({ ...o, [key]: '' }), {});
    const createTableHeaders = arr => arr.map(item => ({ 'id': item, 'text': intlApi.formatMessage({ id: labelNames[item] }) }));

    // Delegation pages for AGENCY_GROUP and CSR_GROUP should be rendered full width.
    const containerWidth = (roleGroup === USER_GROUPS.AGENCY_GROUP || roleGroup === USER_GROUPS.CSR_GROUP) ?
        'grid-col-12' : 'grid-col-9';

    const filtersData = createFilterObj(filtersByRole[roleGroup]);
    const tableHeadersData = tableHeadersByRole[roleGroup];

    const [householdCaseIdFlag, setHouseholdCaseIdFlag] = useState(true);
    const [pageNumber, setPageNumber] = useState(0);
    const prevHouseholdCaseIdFlag = usePrevious(householdCaseIdFlag);

    const backToAdminLink = isState.CA ? externalEndpoints.CA_LINKS.CA_BACK_TO_ADMIN_SEARCH.processReplacements({ CALHEERS_URL: links.CALHEERS_URL }) : null;

    useEffect(() => {
        if ([ALL_USER_ROLES.BROKER, ALL_USER_ROLES.ASSISTER].includes(roleGroup)) {
            actions.fetchLeftNavParams(endpoints.AGENT_LEFT_NAV_PARAMS);
        }
        designationHistoryEnabled && actions.initDelegationFilters(filtersData);
        // Only for CSR group householdCaseId should be pre populated.
        if (roleGroup === USER_GROUPS.CSR_GROUP || roleGroup === USER_GROUPS.ADMIN) {
            const householdCaseId = localStorage.getItem('householdCaseId') || '';
            actions.setFilterValue({ name: 'householdCaseId', value: householdCaseId });
        }
        setHouseholdCaseIdFlag(false);

    }, []);

    useEffect(() => {
        if (prevHouseholdCaseIdFlag !== undefined && prevHouseholdCaseIdFlag) {
            const filtersQueryParams = constructQueryParams(filters);
            const fetchStringParams = filtersQueryParams && `&${filtersQueryParams}`;

            actions.fetchDelegationData(`${endpoints.DELEGATION_HISTORY.processReplacements({ pageNumber: pageNumber + 1 })}${fetchStringParams}`);
        }
    }, [filters.householdCaseId, prevHouseholdCaseIdFlag]);


    /**
     * Creates string of non empty filters values.
     * @param filters
     * @returns {any}
     */
    const constructQueryParams = filters => Object.keys(filters).map(key => filters[key] && `${key}=${filters[key]}`).filter(Boolean).join('&');

    const getHouseholdPrimaryName = (condition, { encryptedHouseholdId, primaryContactFirstName, primaryContactLastName } ) => {
        return condition ? 
            <a 
                className="gi-txt__capitalize" 
                href={endpoints.VIEW_MEMBER.processReplacements({ encryptedHouseholdId: encryptedHouseholdId })}
            >
                {primaryContactFirstName.toLowerCase()} {primaryContactLastName.toLowerCase()}
            </a> :
            <span className="gi-txt__capitalize">{primaryContactFirstName.toLowerCase()} {primaryContactLastName.toLowerCase()}</span>;
    };

    /**
     * Formats delegation history table data: concats firstName and lastName.
     * @param delegationHistory
     * @returns {[]}
     */
    const processedTableData = (delegationHistory) => {
        let result = [];
        delegationHistory.forEach(row => {
            let processedRow = {};
            for (let headerName of tableHeadersData) {
                switch (headerName) {
                    case fields.HOUSEHOLD_PRIMARY: {
                        let condition = roleGroup === USER_GROUPS.CSR_GROUP ? row[fields.HOUSEHOLD_CASE_ID] : !row[fields.DESIGNATION_END_DATE] && row[fields.HOUSEHOLD_CASE_ID];
                        condition = enrollmentDetailsEnabled && condition;
                        processedRow[headerName] = getHouseholdPrimaryName(condition, row);
                        break;
                    }
                    case fields.AGENT_CEC_NAME:
                    case fields.AGENT_NAME: {
                        const firstName = row.agentCecFirstName ? row.agentCecFirstName.toLowerCase() : '';
                        const lastName = row.agentCecLastName ? row.agentCecLastName.toLowerCase() : '';
                        processedRow[headerName] =  <span className="gi-txt__capitalize">{firstName} {lastName}</span>;
                        break;
                    }
                    case fields.DESIGNATION_END_DATE: {
                        processedRow[headerName] = row[headerName] ? row[headerName]  : 'Present';
                        break;
                    }
                    case fields.REASON: {
                        processedRow[headerName] = !row[headerName] ? '-' : intlApi.formatMessage({ id: DELEGATION_REASONS[row[headerName]] });
                        break;
                    }
                    default: {
                        processedRow[headerName] = row[headerName] ? row[headerName] : '-';
                        break;
                    }
                }
            }
            result.push(processedRow);
        });
        return result;
    };

    /**
     * Filter input onChange handler. Dispatches action to save values to a redux store.
     * @param e
     */
    const onChangeHandler = e => {
        const { name, value } = e.target;
        actions.setFilterValue({ name, value });
    };

    /**
     * Collects values from all filters, constructs query param string and fetches new delegation data.
     */
    const onApplyHandler = () => {
        const params = constructQueryParams(filters);
        const queryParams = params && `&${params}`;
        actions.fetchDelegationData(`${endpoints.DELEGATION_HISTORY.processReplacements({ pageNumber: 1 })}${queryParams}`);
        setPageNumber(0);
    };

    /**
     * Clears filters to initial state and updates redux store.
     */
    const onResetHandler = () => {
        actions.resetFilterValues(filtersData);
        setPageNumber(0);
        actions.fetchDelegationData(`${endpoints.DELEGATION_HISTORY.processReplacements({ pageNumber: 1 })}`);
    };

    /**
     * Makes a new call to fetch new set of data.
     * @param data
     */
    const onPageNumberChange = data => {
        const params = constructQueryParams(filters);
        const queryParams = params && `&${params}`;
        setPageNumber(data.selected);
        actions.fetchDelegationData(`${endpoints.DELEGATION_HISTORY.processReplacements({ pageNumber: data.selected + 1 })}${queryParams}`);
    };


    const LeftNav = () => {
        let navLinksByRole = '';
        const { switchAccUrl, encryptedBrokerAssisterId, selfDelegationSwitchAccUrl } = leftNavParams;

        switch (roleGroup) {
            case ALL_USER_ROLES.BROKER:
                navLinksByRole = brokerNavLinks;
                break;
            case ALL_USER_ROLES.ASSISTER:
                navLinksByRole = assisterNavLinks;
                break;
            default:
                break;

        }

        return (
            (roleGroup === ALL_USER_ROLES.BROKER || roleGroup === ALL_USER_ROLES.ASSISTER) &&
            <aside className="usa-layout-docs__sidenav desktop:grid-col-3" id="side-nav">
                {leftNavParams && Object.keys(leftNavParams).length > 0 &&
                    <SideNav
                        {...{ stateCode }}
                        navLinks={navLinksByRole}
                        userRole={role}
                        visibilityParams={{ ...leftNavParams, ticketEnabled }}
                        replacements={{ switchAccUrl, encryptedBrokerAssisterId, selfDelegationSwitchAccUrl, lang: language === 'en' ? 'en_US' : 'es_ES' }}
                    />}
            </aside>);
    };

    const delegationViewProps = { 
        ...{
            isState,
            roleGroup,
            filters,
            spinner, 
            pagination,
            pageNumber,
            backToAdminLink,
            onChangeHandler,
            onResetHandler,
            onApplyHandler,
            onPageNumberChange 
        },
        tableData: processedTableData(history),
        tableColumns: createTableHeaders(tableHeadersData) 
    };

    const getAllowedUserRolesForNavBar = () => {
        const allowedUserRoles = [
            USER_GROUPS.AGENCY_GROUP,
            ALL_USER_ROLES.BROKER,
            ALL_USER_ROLES.ASSISTER,
        ];

        !isState.CA && allowedUserRoles.push(USER_GROUPS.CSR_GROUP);

        return allowedUserRoles;
    };

    return (designationHistoryEnabled ?
        roleGroup === ALL_USER_ROLES.INDIVIDUAL  ?
            // Delegation as part of Individual Portal Container(it has it's own sideNav and layout)
            <div id="delegation" className="delegation">
                <DelegationView {...delegationViewProps}/>

            </div> :

        // Delegation default as a separate page
            <div id="delegation" className="usa-layout-docs usa-section gi-main-section delegation">
                <div className="grid-container grid-widescreen gi-grid-container">
                    <div className="grid-row">
                        <NavBarContainer
                            navBarClass={!isState.CA ? 'grid-col-12 delegation-top-nav' : ''}
                            roleGroup={roleGroup}
                            arrayOfUserRoles={getAllowedUserRolesForNavBar()}
                        />
                    </div>
                    <div className="grid-row grid-gap">
                        <LeftNav/>
                        <div className={`usa-layout-docs__main desktop:${containerWidth}`} id="main-content">
                            <DelegationView {...delegationViewProps}/>
                        </div>
                    </div>
                </div>
            </div> : <p> Delegation history is not enabled</p>
    );
};

const mapStateToProps = state => ({
    spinner: state.common.spinner,
    environment: state.common.environment,
    delegation: state.delegation
});
const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        fetchDelegationData,
        initDelegationFilters,
        setFilterValue,
        resetFilterValues,
        fetchNavBarData,
        fetchLeftNavParams
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(DelegationContainer);
